import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { mutate } from '../graphql/client';
import { ADD_REACTION, REMOVE_REACTION } from '../graphql/operations';
import { invalidateCache, getCommitCacheKey } from '../utils/cache';
import { REACTION_FIELD_MAP, type ReactionType, type ReactionCounts } from '../types/api';

interface UseReactionsResult {
  counts: ReactionCounts;
  userReactions: ReactionType[];
  toggleReaction: (type: ReactionType) => Promise<void>;
  loading: boolean;
}

export function useReactions(
  commitId: string,
  initialCounts: ReactionCounts,
  initialUserReactions: ReactionType[]
): UseReactionsResult {
  const [counts, setCounts] = useState<ReactionCounts>(initialCounts);
  const [userReactions, setUserReactions] = useState<ReactionType[]>(initialUserReactions);
  const [loading, setLoading] = useState(false);

  const toggleReaction = useCallback(async (type: ReactionType) => {
    const field = REACTION_FIELD_MAP[type];
    const hasReaction = userReactions.includes(type);

    // Optimistic update
    if (hasReaction) {
      setUserReactions(prev => prev.filter(r => r !== type));
      setCounts(prev => ({ ...prev, [field]: Math.max(0, prev[field] - 1) }));
    } else {
      setUserReactions(prev => [...prev, type]);
      setCounts(prev => ({ ...prev, [field]: prev[field] + 1 }));
    }

    setLoading(true);
    try {
      if (hasReaction) {
        await mutate(REMOVE_REACTION, { commitId, type });
      } else {
        await mutate(ADD_REACTION, { commitId, type });
      }
      // Invalidate cache on success so data stays in sync
      invalidateCache(getCommitCacheKey(commitId));
    } catch (error) {
      // Rollback on error
      if (hasReaction) {
        setUserReactions(prev => [...prev, type]);
        setCounts(prev => ({ ...prev, [field]: prev[field] + 1 }));
      } else {
        setUserReactions(prev => prev.filter(r => r !== type));
        setCounts(prev => ({ ...prev, [field]: Math.max(0, prev[field] - 1) }));
      }
      // Show error to user
      toast.error('Failed to update reaction');
      console.error('Failed to toggle reaction:', error);
    } finally {
      setLoading(false);
    }
  }, [commitId, userReactions]);

  return { counts, userReactions, toggleReaction, loading };
}
